import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Users,
  Search,
  ChevronRight,
  Phone,
  FileCheck,
  Ticket
} from 'lucide-react'
import { useAuth } from '../App'
import { AgentAPI, Passenger, Allocation } from '../services/api'
import Header from '../components/Header'
import BottomNav from '../components/BottomNav'

const Passengers: React.FC = () => {
  const { logout } = useAuth()
  const [passengers, setPassengers] = useState<(Passenger & { series_name?: string; series_code?: string })[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const fetchAllPassengers = async () => {
      try {
        setLoading(true)
        setError(null)
        // Get all allocations first
        const allocations: Allocation[] = await AgentAPI.getMyAllocations()
        
        // Fetch passengers for each allocation
        const allPassengers: (Passenger & { series_name?: string; series_code?: string })[] = []
        for (const alloc of allocations) {
          try {
            const pax = await AgentAPI.getMyPassengers(alloc.id)
            pax.forEach(p => {
              allPassengers.push({
                ...p,
                series_name: alloc.series_name,
                series_code: alloc.series_code
              })
            })
          } catch (err) {
            // Skip if allocation has no passengers
          }
        }
        setPassengers(allPassengers)
      } catch (err: any) {
        setError(err.message || 'Failed to load passengers')
      } finally {
        setLoading(false)
      }
    }
    fetchAllPassengers()
  }, [])

  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800'
      case 'tentative': return 'bg-amber-100 text-amber-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getVisaStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600'
      case 'applied': return 'text-blue-600'
      case 'documents_collected': return 'text-amber-600'
      case 'rejected': return 'text-red-600'
      default: return 'text-gray-500'
    }
  }

  const filteredPassengers = passengers.filter(p =>
    p.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.passport_no && p.passport_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.cnic && p.cnic.includes(searchTerm))
  )

  return (
    <div className="min-h-screen bg-gray-100 pb-safe">
      <Header title="All Passengers" showLogout onLogout={logout} />

      <div className="p-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, passport, or CNIC"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Stats */}
        <div className="bg-white rounded-xl p-4">
          <p className="text-sm text-gray-500">Total Passengers</p>
          <p className="text-3xl font-bold text-gray-900">{passengers.length}</p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Passengers List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : filteredPassengers.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">
              {searchTerm ? 'No passengers match your search' : 'No passengers added yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPassengers.map((passenger, index) => (
              <motion.div
                key={passenger.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{passenger.full_name}</h4>
                    <p className="text-xs text-gray-500">{passenger.series_name || 'Unknown Series'}</p>
                    <p className="text-xs text-gray-400 capitalize">{passenger.room_type} sharing</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBookingStatusColor(passenger.booking_status)}`}>
                    {passenger.booking_status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 text-sm mt-3">
                  {passenger.mobile_number && (
                    <a
                      href={`tel:${passenger.mobile_number}`}
                      className="flex items-center gap-1 text-primary-600"
                    >
                      <Phone size={14} />
                      {passenger.mobile_number}
                    </a>
                  )}
                  <div className={`flex items-center gap-1 ${getVisaStatusColor(passenger.visa_status)}`}>
                    <FileCheck size={14} />
                    <span className="capitalize">{passenger.visa_status.replace('_', ' ')}</span>
                  </div>
                  <div className={`flex items-center gap-1 ${passenger.ticket_status === 'issued' ? 'text-green-600' : 'text-gray-500'}`}>
                    <Ticket size={14} />
                    <span className="capitalize">{passenger.ticket_status}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                  <div className="text-sm">
                    <span className="text-gray-500">Paid: </span>
                    <span className={passenger.payment_status === 'full' ? 'text-green-600' : 'text-amber-600'}>
                      PKR {passenger.amount_paid.toLocaleString()} / {passenger.sale_price.toLocaleString()}
                    </span>
                  </div>
                  <ChevronRight size={18} className="text-gray-400" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BottomNav activeTab="passengers" />
    </div>
  )
}

export default Passengers

